import { useCallback, useRef } from "react";
import { useComposeActions, type DraftFolderRefreshSpec } from "@/hooks/useComposeActions";
import { clearFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import { syncFolderHeaders } from "@/lib/mail/folderWarmup";
import type { MailScheduledSendEntry } from "@/lib/api/mailScheduledSend";
import type { OutboxEnqueuePayload, OutboxEnqueueResult } from "@/lib/outbox/outboxQueue";
import type { MailAccountSummary, MailMessageDetail, MailMessageSummary } from "@/lib/types/mail";

type Options = {
  account: MailAccountSummary | null;
  accounts?: MailAccountSummary[];
  folders?: import("@/lib/types/mail").MailFolder[];
  folder: string;
  messages: MailMessageSummary[];
  readerMessage: MailMessageDetail | null;
  removeMessages: (uids: number[]) => void;
  reloadInbox: (
    refresh?: boolean,
    options?: { background?: boolean; silent?: boolean }
  ) => Promise<void>;
  reloadFolders: (refresh?: boolean) => Promise<void | boolean>;
  isOutboxView?: boolean;
  onComposeOpenChange?: (open: boolean) => void;
  onEnqueue: (payload: OutboxEnqueuePayload) => OutboxEnqueueResult | void;
  onScheduled?: (entry: MailScheduledSendEntry) => void;
  onScheduleBegin?: (info: {
    accountId: number;
    sendAt: string;
    subject: string;
  }) => void;
  onScheduleEnd?: () => void;
  onSavingDraftsChange?: (pending: boolean, accountId?: number) => void;
};

export function useMobileCompose({
  account,
  accounts = [],
  folders = [],
  folder,
  messages,
  readerMessage,
  removeMessages,
  reloadInbox,
  reloadFolders,
  isOutboxView = false,
  onComposeOpenChange,
  onEnqueue,
  onScheduled,
  onScheduleBegin,
  onScheduleEnd,
  onSavingDraftsChange,
}: Options) {
  const folderRef = useRef(folder);
  folderRef.current = folder;

  const refreshDraftFolder = useCallback(
    async ({ accountId: id, draftFolder, afterSave = false }: DraftFolderRefreshSpec) => {
      clearFolderSnapshot(id, draftFolder);
      const viewingDraftFolder = folderRef.current === draftFolder;

      if (viewingDraftFolder) {
        if (afterSave) {
          await syncFolderHeaders(id, draftFolder);
          await reloadInbox(true, { background: true, silent: true });
        } else {
          await reloadInbox(false, { background: true, silent: true });
        }
      } else if (afterSave) {
        void syncFolderHeaders(id, draftFolder);
      }

      void reloadFolders(true);
    },
    [reloadFolders, reloadInbox]
  );

  return useComposeActions({
    accounts,
    account: account ?? undefined,
    selectedFolder: folder,
    folders,
    isOutboxView,
    displayMessages: messages,
    readerMessage,
    refreshDraftFolder,
    removeInboxMessage: (uid) => removeMessages([uid]),
    onEnqueue,
    onScheduled,
    onScheduleBegin,
    onScheduleEnd,
    onSessionChange: onComposeOpenChange,
    onSavingDraftsChange,
  });
}
